// Shared topology definitions; values and helpers are maintained here.
import { FIRST_CALCULATION_TEXT } from './analysis';
import type { AuditWindow } from './analysis';
import { tzAbbrev } from '../rfHealth';


// --- Backbone tab (2026-09-30) — GET /api/audit/backbone, MeshCore-only ----
// Ported from the approved prototype (backbone-mm7.html + the review-round-4
// fixes) into the real app's MapLibre/Audit conventions. No rule dropdown:
// regional-backbone classification is a single decided rule (Tukey fence,
// OR a cut point, OR a high in-region degree ratio — audit/backbone.py's
// own module docstring has the full rule set) that lives in Settings, not
// a client-side toggle.
export type BackboneClass = "both" | "backhaul" | "regional_backbone" | "neither";


export interface BackboneClassInfo {
  label: string;
  color: string;
}


// MeshMapper-style class swatch colours — fixed hex, not theme-paired: these
// are semantic markers painted straight into GL circle/icon paint
// properties (not CSS), so they stay the same regardless of light/dark
// basemap theme, same as the basemap tiles themselves.
//
// 2026-09-30 pill-colour task: `color` is now the ONE class colour, used
// everywhere a class needs to be shown — the pill's own outline stroke
// (see makePillImage's call sites below), the dot underneath it, the
// legend swatch, and the detail panel's class badge. The pill's own FILL
// is always black and its text always white (BACKBONE_PILL_FILL_COLOR /
// BACKBONE_PILL_TEXT_COLOR below), regardless of class, so there is no
// longer a separate "stroke" shade to track per class.
export const BACKBONE_CLASS_INFO: Record<BackboneClass, BackboneClassInfo> = {
  // Never shown as a word: a repeater in both classes shows the Backhaul and Regional
  // backbone pills side by side; this colour is only the map dot / legend swatch.
  both: { label: "Backhaul + Regional backbone", color: "#D4AF37" },
  backhaul: { label: "Backhaul", color: "#22C55E" },
  regional_backbone: { label: "Regional backbone", color: "#a78bfa" },
  neither: { label: "Other", color: "#9aa0a6" },
};


// Every repeater name pill on the Backbone map: black fill, white text,
// class-coloured outline (BACKBONE_CLASS_INFO[cls].color) — same in light
// and dark app theme, since the map's own basemap never changes with it
// (buildBackboneMapStyle's own comment). Defined once here rather than
// inlined at each call site.
export const BACKBONE_PILL_FILL_COLOR = "#111214";

export const BACKBONE_PILL_TEXT_COLOR = "#ffffff";

// The dot's own ring (circle-stroke-color) — one shared neutral colour
// for every class, so the dot's FILL (the class colour) is what actually
// distinguishes classes, same as the pill.
export const BACKBONE_DOT_STROKE_COLOR = "#1b1c20";


// A node's `classes` field can hold up to three entries at once
// (["backhaul","regional_backbone","both"]) — this collapses that into the
// one display class, same precedence the prototype used.
// The class pills a node shows: the two real classes for a dual-class node.
export function backboneClassPills(cls: BackboneClass): BackboneClass[] {
  return cls === "both" ? ["backhaul", "regional_backbone"] : [cls];
}


export function backboneNodeClass(classes: string[]): BackboneClass {
  if (classes.includes("both")) return "both";
  if (classes.includes("backhaul")) return "backhaul";
  if (classes.includes("regional_backbone")) return "regional_backbone";
  return "neither";
}


export type BackboneLinkKind = "backhaul" | "backbone" | "faint";


// 2026-09-30 pill-colour task: backhaul links now match the class colour
// (BACKBONE_CLASS_INFO.backhaul.color, green) instead of the old orange —
// regional backbone links keep their existing purple, other stable links
// keep their existing gray.
export const BACKBONE_LINK_KIND_INFO: Record<BackboneLinkKind, { label: string; color: string; width: number }> = {
  backhaul: { label: "Backhaul link", color: "#22C55E", width: 3.2 },
  backbone: { label: "Regional backbone link", color: "#a78bfa", width: 2.2 },
  faint: { label: "Other stable link", color: "#9aa0a6", width: 1.2 },
};


export const BACKBONE_DEFAULT_WINDOW: AuditWindow = "7d";

export const BACKBONE_TAB_TITLE = "Topology";

// Neighbour links on the Topology map: dotted blue, off by default.
export const NEIGHBOUR_LINK_COLOR = "#2a78d6";

export const TOPOLOGY_NEIGHBOUR_LINKS_LABEL = "Neighbour Links";

export const TOPOLOGY_NEIGHBOUR_LINKS_TIP =
  "The first repeater to hear a node's own packets, and the repeater that handed a packet to an observer; stable links only";

// One line, normal case — not the uppercase `.page-section-title`
// treatment (that class is for short labels, not hand-written prose).
export const BACKBONE_TAB_INTRO = "Repeaters classified as backhaul or regional backbone, from the stable relay-to-relay link graph.";

export const BACKBONE_SETTINGS_LINK_LABEL = "Settings: Topology & scopes";

export const BACKBONE_LOADING = "Loading the backbone graph…";

export const BACKBONE_UNAVAILABLE = "Backbone data didn't respond as expected.";

export const BACKBONE_EMPTY = "No positioned repeaters in this window.";

export const BACKBONE_PENDING = FIRST_CALCULATION_TEXT;


// Single merged legend/key (review-round-4 fix 5: one legend, not a top bar
// plus a separate collapsed one) — the class swatches AND the link key
// live in the one panel this registry backs. Collapsed by default,
// state persisted in localStorage (2026-09-30 fix 2). Trimmed to labels
// only (2026-09-30 fix 3): the old "No clustering" explanatory
// paragraph, the counts paragraph, and the multi-line solid/dashed/
// "Direct" prose are gone entirely — the counts moved to one compact
// line outside the legend (see `backboneCompactCountsLine`), and the
// solid-vs-dashed distinction is now the one short line below.
export const BACKBONE_LEGEND_PANEL_TITLE = "Legend";

export const BACKBONE_NEIGHBOURS_SHOW_ALL = (count: number) => `Show all (${count})`;

export const BACKBONE_LEGEND_CLOSE_LABEL = "Close legend";

export const BACKBONE_LEGEND_HEADING = "Repeater class";

export const BACKBONE_LEGEND_TOGGLE_NEITHER = "Show other repeaters";

export const BACKBONE_LEGEND_TOGGLE_FAINT = "Show all other stable links";

export const BACKBONE_LEGEND_LINKS_HEADING = "Links";

// 2026-09-30 fix 3: one short line, replacing the old multi-line
// direction/"Direct" prose.
export const BACKBONE_LEGEND_DIRECTION_NOTE = "Solid = traffic flows both ways. Dashed = one way only.";


// 2026-09-30 layout task: the old "Live data, ... UTC" line (this
// function's old base string) is no longer shown in the normal case — see
// BackboneTab.tsx's own comment on its toolbar row. This now returns ONLY
// the stale-data warning, and only when the newest real event
// (`repeater_link_events` row) is older than the owner's own
// `audit.backbone_stale_after_hours` setting; `null` means data is fresh
// enough that nothing should be shown at all.
export function backboneStaleWarning(newestEventAtIso: string | null, staleAfterHours: number, nowMs: number = Date.now()): string | null {
  if (!newestEventAtIso) return null;
  const newestMs = new Date(newestEventAtIso).getTime();
  if (Number.isNaN(newestMs)) return null;
  const ageHours = (nowMs - newestMs) / 3_600_000;
  if (ageHours <= staleAfterHours) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  const d = new Date(newestEventAtIso);
  const fmt = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const tz = tzAbbrev(newestEventAtIso);
  const ago = Math.round(ageHours);
  return `newest data ${fmt}${tz ? ` ${tz}` : ""} (${ago}h ago)`;
}


// 2026-09-30 fix 3: the one compact counts line shown OUTSIDE the legend
// (above/below the map), replacing the old two-paragraph counts+totals
// breakdown that used to live inside the legend itself. "Backhaul" and
// "Regional backbone" here are each already the IN-TOTAL figure (both +
// that class alone), matching the totals the old second paragraph gave.
export function backboneCompactCountsLine(both: number, backhaulOnly: number, regionalOnly: number, other: number): string {
  return `${BACKBONE_CLASS_INFO.backhaul.label} ${both + backhaulOnly} · ${BACKBONE_CLASS_INFO.regional_backbone.label} ${both + regionalOnly} · ${BACKBONE_CLASS_INFO.neither.label} ${other}`;
}


// --- Backbone details panel -------------------------------------------------
export const BACKBONE_DETAILS_TITLE = "Details";

export const BACKBONE_DETAILS_EXIT_LABEL = "Exit detail view";

export const BACKBONE_SECTION_WHY = "Why It’s Classified";

export const BACKBONE_SECTION_WHY_NOT = "Why It’s Not Classified";

// The region-versus-scope explanation lives in hover tips on the Region and Scopes section headers.
export const BACKBONE_REGION_TIP = "Region: where the radio sits.";

export const BACKBONE_SCOPES_TIP = "Scope: the #tag it advertises.";

export const BACKBONE_WHY_NOT_PER_UNIT = "Per Region And Scope";

// Only shown for a CLASSIFIED repeater, collapsed by default (a <details>
// element) — the units it didn't qualify in, kept out of the way of the
// reasons it WAS classified for (never a "why not" next to those).
export const BACKBONE_SECTION_ALSO_JUDGED = "Also judged in";

// 2026-09-30 fix 3: one-line description under the heading, in the same
// muted caption style the Metrics section already uses under each row.
export const BACKBONE_SECTION_ALSO_JUDGED_DESC =
  "The other regions and local scopes this repeater was checked against, and why it didn’t qualify there (share within that unit).";

export const BACKBONE_SECTION_TRAFFIC = "Traffic";

export const BACKBONE_SECTION_REGION = "Region";

export const BACKBONE_SECTION_SCOPES = "Scopes";

export const BACKBONE_SECTION_FOOTPRINT = "Footprint";

export const BACKBONE_SECTION_METRICS = "Metrics";

export const BACKBONE_SECTION_LAST_HEARD = "Last heard";

export const BACKBONE_TRAFFIC_NOT_RANKED = "Not ranked in a region.";

export const BACKBONE_REGION_NOT_VERIFIED = "Not verified for any region.";

export const BACKBONE_SCOPES_NONE = "None recorded.";

export const BACKBONE_NEIGHBOURS_NONE = "No stable links recorded.";

// Review-round-4 fix 6: mark a reason that rests on a self/other end's
// inferred region or scope (an audit found 41 of 44 backhaul links rely on
// inferred data on at least one end) rather than let it read as certain.
export const BACKBONE_REASON_INFERRED_SUFFIX = " (rests on inferred data — see the Region/Scopes source below)";


export type BackboneSourceKind = "confirmed" | "conflict" | "inferred" | "none";

export const BACKBONE_SOURCE_CHIP_LABEL: Record<BackboneSourceKind, string> = {
  confirmed: "Confirmed",
  conflict: "Conflicting",
  inferred: "Inferred",
  none: "Not verified",
};


export function backboneRegionSourceTitle(source: BackboneSourceKind, note: string | null, neighbourCount: number | null): string {
  if (source === "conflict") return note ?? "Its own reported position disagrees with its estimated position from nearby repeaters.";
  if (source === "confirmed") return "Confirmed by this repeater’s own reported position.";
  if (source === "inferred") return `Inferred from the positions of ${neighbourCount ?? "nearby"} neighbouring repeaters.`;
  return "No reported or estimated position places this repeater in a known region.";
}


export function backboneScopeSourceTitle(source: BackboneSourceKind, dominancePct: number | null, days: number | null): string {
  if (source === "confirmed") return "Confirmed home scope, as declared in its own configuration.";
  if (source === "inferred") {
    const pct = dominancePct !== null && dominancePct !== undefined ? `${dominancePct.toFixed(0)}%` : "an unspecified share";
    return `Inferred from its own relayed traffic, ${pct} over ${days ?? "an unknown number of"} days.`;
  }
  return "No declared or inferable home scope.";
}


// --- Backbone metrics captions ---------------------------------------------
export const BACKBONE_METRIC_REPEATABILITY = "Repeatability";

export const BACKBONE_METRIC_REPEATABILITY_CAPTION = "Share of the hours in the window it was seen relaying, out of the hours its region had traffic.";

export const BACKBONE_METRIC_STABILITY = "Dropouts (lower is better)";

// 2026-09-30 fix 1: rewritten to truthfully describe the decayed
// instability score (audit/backbone.py's `_compute_stability`) — it rises
// each time the repeater drops out unexpectedly, fades back down over
// time after each one, and reads 100% for as long as it's still silent
// right now. Displayed value is clamped at 100% (it's an uncapped decayed
// score, not a fraction) -- see BackboneTab.tsx.
export const BACKBONE_METRIC_STABILITY_CAPTION =
  "Rises each time this repeater drops out unexpectedly, fades over time after each one, and shows 100% for as long as it’s still silent right now.";

export const BACKBONE_METRIC_CREDIBILITY = "Credibility";

export const BACKBONE_METRIC_CREDIBILITY_CAPTION = "How many distinct observers heard packets that crossed this repeater’s links, in the selected window.";

export const BACKBONE_METRIC_REACHABILITY = "Reachability";

export const BACKBONE_METRIC_REACHABILITY_CAPTION = "Distinct regions and scopes this repeater’s traffic has reached.";

export const BACKBONE_METRIC_REACHABILITY_LIMITED = "(limited by observer coverage)";

export const BACKBONE_METRIC_LINK_SYMMETRY = "Both-ways links";

export const BACKBONE_METRIC_LINK_SYMMETRY_CAPTION = "Count of stable links this repeater holds where traffic flows both directions.";

export const BACKBONE_METRIC_BETWEENNESS = "Betweenness";

export const BACKBONE_METRIC_BETWEENNESS_CAPTION = "How often it sits on the path between other repeaters; rank 1 = most.";


export function backboneReachabilityValue(regions: number, scopes: number): string {
  return `Reaches ${regions} region${regions === 1 ? "" : "s"} / ${scopes} scope${scopes === 1 ? "" : "s"}`;
}


// Review-round-4 fix 3: "rank #N of M", not a bare rank.
// 2026-09-30 fix 4: name the M -- it's the count of repeaters with stable
// links (audit/backbone.py's own betweenness population, mesh-wide, not
// this page's total repeater count), which otherwise reads as an
// unexplained, mismatched denominator.
export function backboneBetweennessValue(rank: number | null, rankOf: number | null): string {
  if (rank === null || rank === undefined) return "—";
  return rankOf ? `rank #${rank} of ${rankOf} repeaters with stable links` : `rank #${rank}`;
}


export const BACKBONE_SECTION_NEIGHBOURS = (count: number) => `Neighbours (${count})`;

export function backboneNeighbourSymmetryLabel(bothWays: boolean, arrow: string | null): string {
  return bothWays ? "both ways" : `one way (${arrow ?? "↔"})`;
}

export function backboneNeighbourMeta(transmissions: number, days: number, windowDays: number): string {
  return `${transmissions} packets over the ${windowDays}-day window · seen on ${days} day${days === 1 ? "" : "s"}`;
}


// --- Backbone link hover tooltip --------------------------------------------
export function backboneLinkDirectionLabel(symmetry: string, direction: string | null, aName: string, bName: string): string {
  if (symmetry === "both_ways") return "both ways";
  if (direction === "a_to_b") return `one way (${aName} → ${bName})`;
  if (direction === "b_to_a") return `one way (${bName} → ${aName})`;
  return "one way";
}

export function backboneLinkPositionNote(aName: string, bName: string, aEstimated: boolean, bEstimated: boolean): string | null {
  const names = [aEstimated ? aName : null, bEstimated ? bName : null].filter((n): n is string => n !== null);
  return names.length ? `position estimated for ${names.join(", ")}` : null;
}

export function backboneDistanceLabel(km: number | null): string {
  return km !== null && km !== undefined ? `${km.toFixed(1)} km` : "distance unknown";
}

export function backboneLinkSummary(transmissions: number, days: number): string {
  return `${transmissions} packets · seen ${days} day${days === 1 ? "" : "s"}`;
}
